import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Clock, Lock } from 'lucide-react';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { Field, Input, Select, Switch } from '../ui/Field';
import { useConfirm } from '../ui/ConfirmProvider';
import { pillDangerGhost, pillPrimary, pillSecondary } from '../ui/pills';
import { LIBELLES_TYPE_SERVICE, NIVEAUX_CLOISONNEMENT, messageErreur, organisationApi, type NiveauCloisonnement, type ServiceFiche, type TypeService } from '../../lib/organisationApi';
import { EtatEchec, Pastille, Squelettes } from './EtatsPage';
import { useOrganisation } from './OrganisationContext';

/** Fiche service (maquette L01-04) : société, SIRET, TVA et compte de paiement propres à ce service. */

export const ServicePage: React.FC = () => {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const confirmer = useConfirm();
    const { contexte, recharger } = useOrganisation();
    const [service, setService] = useState<ServiceFiche | null>(null);
    const [erreur, setErreur] = useState<unknown>(null);
    const [form, setForm] = useState({ name: '', type: 'restaurant' as TypeService, societe: '', siret: '', numero_tva: '', responsable_nom: '', autre_societe: false, cloisonnement: 'tout_voir' as NiveauCloisonnement });
    const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);
    const [envoi, setEnvoi] = useState(false);

    const charger = useCallback(async () => {
        setErreur(null);
        try {
            const s = await organisationApi.service(id);
            setService(s);
            setForm({ name: s.name, type: s.type, societe: s.societe, siret: s.siret, numero_tva: s.numero_tva, responsable_nom: s.responsable_nom, autre_societe: s.autre_societe, cloisonnement: s.cloisonnement });
        } catch (e) { setErreur(e); }
    }, [id]);
    useEffect(() => { void charger(); }, [charger]);

    const siretNet = form.siret.replace(/\s/g, '');
    const siretErreur = siretNet !== '' && !/^\d{14}$/.test(siretNet) ? 'Le SIRET comporte 14 chiffres.' : '';
    const tvaNet = form.numero_tva.replace(/\s/g, '').toUpperCase();
    const tvaErreur = tvaNet !== '' && !/^[A-Z]{2}[A-Z0-9]{2,12}$/.test(tvaNet) ? 'Format attendu : FR12 901234567.' : '';
    const maj = (k: 'name' | 'societe' | 'siret' | 'numero_tva' | 'responsable_nom') => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const valide = form.name.trim() && !siretErreur && !tvaErreur;

    const enregistrer = async () => {
        if (!valide) return;
        setEnvoi(true); setMessage(null);
        try {
            const s = await organisationApi.enregistrerService(id, form);
            setService(s);
            setMessage({ ok: true, texte: 'Service enregistré.' });
            void recharger();
        } catch (e) { setMessage({ ok: false, texte: messageErreur(e) }); }
        finally { setEnvoi(false); }
    };

    const retirer = async () => {
        if (!service) return;
        const ok = await confirmer({ title: `Retirer le service « ${service.name} » ?`, description: 'Le service disparaît de la liste. Vos données sont conservées.', confirmLabel: 'Retirer le service', destructive: true });
        if (!ok) return;
        try { await organisationApi.retirerService(id); void recharger(); navigate('/organisation'); }
        catch (e) { setMessage({ ok: false, texte: messageErreur(e) }); }
    };

    return (
        <div className="space-y-4 max-w-[900px]">
            <Link to="/organisation" className="inline-flex items-center gap-1 min-h-[28px] max-md:min-h-[44px] text-[12.5px] font-semibold text-content-muted hover:text-content"><ChevronLeft size={14} /> Organisation</Link>
            <DashboardHeader titre={service?.name || 'Service'} sousTitre={service ? `Service · ${contexte?.etablissement.name || ''}${service.societe ? ` · ${service.societe}` : ''}` : ' '} />
            {erreur ? <EtatEchec erreur={erreur} titre="Impossible d'afficher ce service" onRetry={charger} /> : !service ? <Squelettes lignes={3} /> : (
                <>
                    {form.autre_societe && (
                        <div role="note" className="flex items-center gap-2 rounded-xl bg-state-warn-soft text-state-warn px-4 py-3 text-[13px]">
                            <Lock size={15} className="shrink-0" /><span><b>Ce service est exploité par une autre société :</b> données non partagées avec le reste de l'établissement.</span>
                        </div>
                    )}
                    <section className="bg-surface border border-border rounded-xl p-5" aria-labelledby="svc-societe">
                        <h2 id="svc-societe" className="text-[15px] font-semibold text-content mb-3">Service et société</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
                            <Field label="Nom du service" required>{(f) => <Input {...f} value={form.name} onChange={maj('name')} maxLength={80} />}</Field>
                            <Field label="Type">{(f) => (
                                <Select {...f} value={form.type} onChange={(e) => setForm((x) => ({ ...x, type: e.target.value as TypeService }))}>
                                    {(Object.keys(LIBELLES_TYPE_SERVICE) as TypeService[]).map((t) => <option key={t} value={t}>{LIBELLES_TYPE_SERVICE[t]}</option>)}
                                </Select>
                            )}</Field>
                            <Field label="Raison sociale">{(f) => <Input {...f} value={form.societe} onChange={maj('societe')} maxLength={120} />}</Field>
                            <Field label="SIRET" error={siretErreur}>{(f) => <Input {...f} value={form.siret} onChange={maj('siret')} inputMode="numeric" className="font-mono" />}</Field>
                            <Field label="N° de TVA intracommunautaire" error={tvaErreur} help="Propre à ce service.">{(f) => <Input {...f} value={form.numero_tva} onChange={maj('numero_tva')} className="font-mono" />}</Field>
                            <Field label="Responsable du service">{(f) => <Input {...f} value={form.responsable_nom} onChange={maj('responsable_nom')} maxLength={80} />}</Field>
                        </div>
                    </section>

                    <section className="bg-surface border border-border rounded-xl p-5 flex flex-wrap items-start justify-between gap-4" aria-labelledby="svc-paiement">
                        <div className="min-w-0 flex-1 basis-[320px]">
                            <h2 id="svc-paiement" className="text-[15px] font-semibold text-content flex items-center gap-2 flex-wrap">
                                Compte de paiement
                                <Pastille ton="warn" icone={<Clock size={11} />}>{service.paiement_statut === 'connecte' ? 'Connecté' : 'Connexion en attente'}</Pastille>
                            </h2>
                            <p className="text-[12.5px] text-content-muted mt-1">Chaque service garde son propre compte Stripe : les encaissements d'un service ne passent pas par celui d'un autre. Tant qu'il n'est pas connecté, les acomptes et prépaiements sont indisponibles.</p>
                        </div>
                        <button type="button" className={pillSecondary(36)} disabled title="La connexion Stripe sera disponible prochainement">Connecter le compte</button>
                    </section>

                    <section className="bg-surface border border-border rounded-xl p-5 space-y-4" aria-labelledby="svc-cloison">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <h2 id="svc-cloison" className="text-[15px] font-semibold text-content">Exploité par une autre société</h2>
                                <p className="text-[12.5px] text-content-muted">Cloisonne les clients, le chiffre d'affaires et la facturation de ce service.</p>
                            </div>
                            <Switch checked={form.autre_societe} onChange={(e) => setForm((f) => ({ ...f, autre_societe: e.target.checked }))} aria-label="Exploité par une autre société" />
                        </div>
                        {form.autre_societe && (
                            <Field label="Ce que la direction de l'établissement voit de ce service" help="Réglage enregistré : aucun filtrage des données n'est appliqué tant qu'il n'a pas été validé.">
                                {(f) => (
                                    <Select {...f} value={form.cloisonnement} onChange={(e) => setForm((x) => ({ ...x, cloisonnement: e.target.value as NiveauCloisonnement }))}>
                                        {NIVEAUX_CLOISONNEMENT.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
                                    </Select>
                                )}
                            </Field>
                        )}
                    </section>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <button type="button" className={pillDangerGhost(36)} onClick={retirer}>Retirer le service</button>
                        <div className="flex items-center gap-3 flex-wrap">
                            {message && <span role="status" className={`text-[12.5px] font-medium ${message.ok ? 'text-state-ok' : 'text-state-danger'}`}>{message.texte}</span>}
                            <button type="button" className={pillPrimary(36)} onClick={enregistrer} disabled={envoi || !valide}>Enregistrer le service</button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ServicePage;
